import React, { useState } from 'react';
import { Card, CardContent, Container, Grid, Typography, Box, Snackbar, Alert } from '@mui/material';
import { School, FileSpreadsheet } from 'lucide-react';
import { MdImportContacts } from 'react-icons/md';
import NewCourseForm from '../../components/common/Forms/NewCourseForm';
import ShowMyCourses from '../../components/common/Forms/ShowMyCoursesList';
import BulkEnrollmentImport from '../../components/common/Forms/BulkEnrollmentImport';
import ConfirmDeleteDialog from '../../components/common/ConfirmDeleteDialog';
import CoursePreview from '../../components/common/CoursePreview';
import CourseDetail from './CourseDetail';
import coursesService from '../../services/CoursesService';

const CoursesModuleMenu = () => {
    const [activeSection, setActiveSection] = useState(null);
    const [editingCourse, setEditingCourse] = useState(null);
    const [previewingCourse, setPreviewingCourse] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [courseToDelete, setCourseToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [alert, setAlert] = useState({ open: false, type: '', message: '' });
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const obj = [
        { 
            name: 'Nuevo Curso', 
            icon: <School size={40} color="#4DABA4" />, 
            onClick: () => {
                setEditingCourse(null);
                setPreviewingCourse(null);
                setActiveSection(activeSection === 'form' ? null : 'form');
            }
        },
        { 
            name: 'Mis Cursos', 
            icon: <MdImportContacts size={40} color="#4DABA4" />, 
            onClick: () => {
                setEditingCourse(null);
                setPreviewingCourse(null);
                setActiveSection(activeSection === 'myCourses' ? null : 'myCourses');
            }
        },
        { 
            name: 'Importar matrículas masivas', 
            icon: <FileSpreadsheet size={40} color="#4DABA4" />, 
            onClick: () => {
                setEditingCourse(null);
                setPreviewingCourse(null);
                setActiveSection(activeSection === 'bulkImport' ? null : 'bulkImport');
            }
        },
    ];

    const handleEditCourse = (courseData) => {
        setEditingCourse(courseData);
        setPreviewingCourse(null);
        setActiveSection('editCourse');
    };

    const handlePreviewCourse = (courseData) => {
        setPreviewingCourse(courseData);
        setEditingCourse(null);
        setActiveSection('previewCourse');
    };

    const handleDeleteCourse = (course) => {
        setCourseToDelete(course);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteCourse = async (courseId) => {
        setIsDeleting(true);
        try {
            await coursesService.deleteCourse(courseId);
            setAlert({
                open: true,
                type: 'success',
                message: 'Curso eliminado exitosamente'
            });
            setDeleteDialogOpen(false);
            setCourseToDelete(null);
            // Trigger refresh of the courses list
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            console.error('Error deleting course:', error);
            setAlert({
                open: true,
                type: 'error',
                message: 'Error al eliminar el curso'
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCourseUpdate = () => {
        setAlert({
            open: true,
            type: 'success',
            message: 'Curso actualizado exitosamente'
        });
        // Permanecer en la pantalla de edición: no cerrar el formulario ni cambiar de sección.
        // El formulario ya conserva en estado local los cambios guardados.
        setActiveSection('editCourse');
        setRefreshTrigger((prev) => prev + 1);
    };

    const handleCancelEdit = () => {
        setEditingCourse(null);
        setPreviewingCourse(null);
        setActiveSection('myCourses');
    };

    const handleCoursePublish = (publishedCourse) => {
        setAlert({
            open: true,
            type: 'success',
            message: 'Curso publicado exitosamente'
        });
        // Return to my courses view and refresh the list
        setPreviewingCourse(null);
        setActiveSection('myCourses');
        setRefreshTrigger(prev => prev + 1);
    };

    const handleClosePreview = () => {
        setPreviewingCourse(null);
        // Keep the current section when closing preview
        // The preview overlay will disappear and show the underlying section
    };

    const handleCourseCreate = (createdCourse) => {
        setAlert({
            open: true,
            type: 'success',
            message: 'Curso creado exitosamente'
        });
        // Switch to my courses view and refresh the list
        setActiveSection('myCourses');
        setRefreshTrigger(prev => prev + 1);
    };

    const handleDuplicateCourse = async (course) => {
        try {
            const duplicated = await coursesService.duplicateCourse(course.id);
            setAlert({
                open: true,
                type: 'success',
                message: 'Curso duplicado exitosamente. Puedes editar la copia de forma independiente.'
            });
            setRefreshTrigger((prev) => prev + 1);

            const duplicatedId = duplicated?.id || duplicated?.Id;
            if (duplicatedId) {
                const fullCourse = await coursesService.getCourseById(duplicatedId);
                handleEditCourse(fullCourse);
            } else {
                setActiveSection('myCourses');
            }
        } catch (error) {
            console.error('Error duplicating course:', error);
            setAlert({
                open: true,
                type: 'error',
                message: error.message || 'Error al duplicar el curso'
            });
            throw error;
        }
    };

    return (
        <Container maxWidth={false} sx={{ mt: 7, px: 0 }}>
            {/* Tarjetas de opciones */}
            <Grid container spacing={3} justifyContent="center">
                {obj.map((card, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card
                            sx={{
                                textAlign: 'center',
                                padding: 2,
                                border: '2px solid transparent',
                                transition: 'border-color 0.3s ease-in-out',
                                backgroundColor: '#f8f9fa',
                                '&:hover': { 
                                    borderColor: '#4DABA4', 
                                    boxShadow: '0px 4px 10px rgba(0,0,0,0.1)' 
                                },
                                cursor: card.onClick ? 'pointer' : 'default',
                            }}
                            onClick={card.onClick}
                        >
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                    {card.icon}
                                </Box>
                                <Typography variant="h6">{card.name}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Content Sections */}
            {activeSection === 'form' && (
                <NewCourseForm 
                    show={true} 
                    editMode={false}
                    onCreate={handleCourseCreate}
                />
            )}
            
            {activeSection === 'myCourses' && !previewingCourse && (
                <ShowMyCourses 
                    show={true} 
                    enableEdit={true}
                    onEditCourse={handleEditCourse}
                    onDeleteCourse={handleDeleteCourse}
                    onPreviewCourse={handlePreviewCourse}
                    onDuplicateCourse={handleDuplicateCourse}
                    refreshTrigger={refreshTrigger}
                />
            )}

            {activeSection === 'bulkImport' && (
                <BulkEnrollmentImport />
            )}
            
            {activeSection === 'editCourse' && editingCourse && (
                <NewCourseForm 
                    show={true} 
                    editMode={true}
                    courseData={editingCourse}
                    onUpdate={handleCourseUpdate}
                    onCancel={handleCancelEdit}
                />
            )}

            {(activeSection === 'previewCourse' || (previewingCourse && activeSection === 'myCourses')) && previewingCourse && (
                <CoursePreview
                    course={previewingCourse}
                    onClose={handleClosePreview}
                    onPublish={handleCoursePublish}
                    isPreviewMode={true}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setCourseToDelete(null);
                }}
                onConfirm={confirmDeleteCourse}
                course={courseToDelete}
                loading={isDeleting}
            />

            {/* Alert Snackbar */}
            <Snackbar 
                open={alert.open} 
                autoHideDuration={6000}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert 
                    severity={alert.type} 
                    onClose={() => setAlert({ ...alert, open: false })}
                    sx={{ width: '100%' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CoursesModuleMenu;
